import { z } from "zod";
import { CLAIM_DENIAL_REASONS, COVERAGE_STATES, ISSUE_STATES, SEVERITIES } from "./types";

/** Request validation shared by the API Worker (mock + live) and backend services. */

const csv = <T extends z.ZodType<unknown, string>>(item: T) =>
  z
    .string()
    .transform((s) => s.split(",").filter(Boolean))
    .pipe(z.array(item));

export const PageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(50),
  cursor: z.string().optional(),
});

export const TraceQuery = PageQuery.extend({
  q: z.string().max(500).optional(),
  agentId: z.string().optional(),
  outcome: z.enum(["success", "failure", "escalated", "abandoned"]).optional(),
  severity: z.enum(SEVERITIES).optional(),
  signal: z.string().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type TraceQuery = z.infer<typeof TraceQuery>;

export const IssueQuery = PageQuery.extend({
  agentId: z.string().optional(),
  state: csv(z.enum(ISSUE_STATES)).optional(),
  severity: csv(z.enum(SEVERITIES)).optional(),
});
export type IssueQuery = z.infer<typeof IssueQuery>;

export const ActivityQuery = PageQuery.extend({
  agentId: z.string().optional(),
  /** Affected party id (e.g. an end user). */
  affectedId: z.string().optional(),
  kind: z.enum(["action", "statement"]).optional(),
  authority: z.enum(["within", "outside", "unknown"]).optional(),
  status: z.enum(["completed", "pending", "failed", "blocked"]).optional(),
  actionClass: z.string().optional(),
  pack: z.string().optional(),
});
export type ActivityQuery = z.infer<typeof ActivityQuery>;

export const ScenarioQuery = z.object({
  policyId: z.string().optional(),
  agentId: z.string().optional(),
  state: z.enum(COVERAGE_STATES).optional(),
});

// ─── Mutations ───────────────────────────────────────────────────────────────

export const IssueTriageBody = z.discriminatedUnion("action", [
  z.object({ action: z.literal("confirm") }),
  z.object({ action: z.literal("dismiss"), reason: z.string().min(10).max(2000) }),
  z.object({ action: z.literal("comment"), note: z.string().min(1).max(5000) }),
  z.object({ action: z.literal("declare_fix"), version: z.string().min(1) }),
  /** A suggested control is enforced by the platform that executes the agent's tools. */
  z.object({ action: z.literal("mitigate"), fixId: z.string() }),
]);
export type IssueTriageBody = z.infer<typeof IssueTriageBody>;

export const ToolConfirmBody = z.object({
  tool: z.string(),
  risk: z.enum([...SEVERITIES, "none"]).optional(),
  actionClass: z.string().optional(),
});
export type ToolConfirmBody = z.infer<typeof ToolConfirmBody>;

export const ClaimCreateBody = z.object({
  /** Optional: the platform matches the claim to the covering policy from the session when omitted. */
  policyId: z.string().optional(),
  /** first_party = the customer's own loss; third_party = loss of an end user / partner. */
  lossType: z.enum(["first_party", "third_party"]).optional(),
  scenarioKey: z.string().optional(),
  claimant: z.string().min(1).max(200),
  traceId: z.string().optional(),
  description: z.string().min(10).max(5000),
  amount: z.number().positive().max(10_000_000),
  currency: z.string().length(3).default("USD"),
});
export type ClaimCreateBody = z.infer<typeof ClaimCreateBody>;

export const PolicyScopedQuery = z.object({ policyId: z.string().optional() });

export const QuoteRequestBody = z.object({
  name: z.string().min(2).max(120),
  agentIds: z.array(z.string()).min(1).max(50),
  scenarios: z.array(z.object({ agentId: z.string(), key: z.string(), subLimit: z.number().int().min(1_000).max(50_000_000) })).min(1).max(200),
  aggregateLimit: z.number().int().min(10_000).max(100_000_000),
  deductible: z.number().int().min(0).max(1_000_000),
  perClaimLimit: z.number().int().min(1_000).max(50_000_000),
  mode: z.enum(["graduated", "strict"]).default("graduated"),
  termMonths: z.literal(12).default(12),
});
export type QuoteRequestBody = z.infer<typeof QuoteRequestBody>;

export const BindQuoteBody = z.object({ quoteId: z.string() });

const OverrideReason = z.string().trim().min(15, "explain the override in at least 15 characters").max(2000);
/** Accept a proposed outcome definition (optionally renaming it or changing its outcome), or dismiss it. */
export const OutcomeDefinitionDecisionBody = z.discriminatedUnion("action", [
  z.object({ action: z.literal("accept"), name: z.string().trim().min(2).max(120).optional(), outcome: z.enum(["resolved", "converted", "partially_resolved", "escalated", "abandoned", "failed"]).optional() }),
  z.object({ action: z.literal("dismiss") }),
]);
export type OutcomeDefinitionDecisionBody = z.infer<typeof OutcomeDefinitionDecisionBody>;

export const OutcomeDefinitionQuery = z.object({ useCaseId: z.string().max(100).optional(), status: z.enum(["proposed", "active", "dismissed"]).optional() });
export type OutcomeDefinitionQuery = z.infer<typeof OutcomeDefinitionQuery>;

/** Underwriter overrides: each names its target and is justified in writing. */
export const UnderwriterOverrideBody = z.discriminatedUnion("kind", [
  /** One extension of an open grace window, by half its length (docs/05 §3.1). */
  z.object({ kind: z.literal("grace_extension"), issueId: z.string().min(1).max(100), reason: OverrideReason }),
  z.object({ kind: z.literal("manual_exclusion"), scenarioId: z.string().min(1).max(100), reason: OverrideReason }),
  z.object({ kind: z.literal("lift_exclusion"), scenarioId: z.string().min(1).max(100), reason: OverrideReason }),
  /** Re-profile and re-score now, as for a material change. */
  z.object({ kind: z.literal("re_rating"), agentId: z.string().min(1).max(200), reason: OverrideReason }),
]);
export type UnderwriterOverrideBody = z.infer<typeof UnderwriterOverrideBody>;

/** An underwriter's decision on a program claim. A denial names its reason and explains it. */
export const ClaimDecisionBody = z.discriminatedUnion("decision", [
  z.object({
    decision: z.literal("approve"),
    /** Defaults to the payable amount; may be lower (a partial settlement, explained in the note). */
    amount: z.number().positive().finite().optional(),
    note: z.string().trim().max(2000).optional(),
  }),
  z.object({
    decision: z.literal("deny"),
    reason: z.enum(CLAIM_DENIAL_REASONS),
    note: z.string().trim().min(10, "explain the denial in at least 10 characters").max(2000),
  }),
]);
export type ClaimDecisionBody = z.infer<typeof ClaimDecisionBody>;

/** Settlement paid: the payment reference (wire, payout id) and when it was made. */
export const ClaimPaymentBody = z.object({
  reference: z.string().trim().min(3).max(200),
  paidAt: z.iso.datetime({ offset: true }).optional(),
});
export type ClaimPaymentBody = z.infer<typeof ClaimPaymentBody>;

/** Reopen a denied claim for review (an appeal or new evidence). */
export const ClaimReopenBody = z.object({ note: z.string().trim().min(10, "explain why in at least 10 characters").max(2000) });
export type ClaimReopenBody = z.infer<typeof ClaimReopenBody>;

export const OrgUpdateBody = z.object({ name: z.string().trim().min(2).max(120) });
export type OrgUpdateBody = z.infer<typeof OrgUpdateBody>;

export const SettingsUpdateBody = z.object({
  privacyMode: z.enum(["full", "redacted", "features"]).optional(),
  retentionDays: z.number().int().min(7).max(730).optional(),
});

// ─── Analytics (docs 14, 15) ────────────────────────────────────────────────

const SESSION_OUTCOME = z.enum(["resolved", "converted", "partially_resolved", "escalated", "abandoned", "failed", "unknown"]);
const FRICTION_KIND = z.enum([
  "rephrase",
  "repeat_question",
  "human_request",
  "negative_sentiment",
  "agent_loop",
  "dead_end",
  "unanswered",
  "tool_failure",
  "slow_response",
  "knowledge_gap",
  "unsupported_request",
  "policy_refusal",
]);

export const SessionQuery = PageQuery.extend({
  q: z.string().max(500).optional(),
  agentId: z.string().optional(),
  useCaseId: z.string().optional(),
  outcome: SESSION_OUTCOME.optional(),
  friction: FRICTION_KIND.optional(),
  signal: z.string().optional(),
  segment: z.string().optional(),
  funnelId: z.string().optional(),
  /** With funnelId: sessions that reached this step and stopped. */
  droppedAfterStep: z.coerce.number().int().min(0).optional(),
  painPointId: z.string().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type SessionQuery = z.infer<typeof SessionQuery>;

/** GET /sessions/:id/similar */
export const SimilarQuery = z.object({
  limit: z.coerce.number().int().min(1).max(25).default(8),
  /** "true": only sessions of the same agent. */
  sameAgent: z.enum(["true", "false"]).default("false").transform((v) => v === "true"),
});

export const AnalyticsScopeQuery = z.object({
  agentId: z.string().optional(),
  useCaseId: z.string().optional(),
});
export type AnalyticsScopeQuery = z.infer<typeof AnalyticsScopeQuery>;

export const UseCaseUpdateBody = z.discriminatedUnion("action", [
  z.object({ action: z.literal("confirm") }),
  z.object({ action: z.literal("rename"), name: z.string().min(2).max(120), description: z.string().max(1000).optional() }),
  z.object({ action: z.literal("merge"), intoId: z.string() }),
  z.object({ action: z.literal("hide") }),
]);
export type UseCaseUpdateBody = z.infer<typeof UseCaseUpdateBody>;

const FunnelStepInput = z.object({
  name: z.string().min(1).max(120),
  kind: z.enum(["event", "tool_call", "semantic", "outcome", "use_case"]),
  match: z.string().min(1).max(1000),
});

export const FunnelCreateBody = z.object({
  name: z.string().min(2).max(120),
  description: z.string().max(1000).default(""),
  agentIds: z.array(z.string()).min(1).max(50),
  steps: z.array(FunnelStepInput).min(2).max(12),
  window: z.enum(["session", "24h", "7d"]).default("session"),
  /** The suggestion this funnel was created from (marked accepted). */
  suggestionId: z.string().max(64).optional(),
});
export type FunnelCreateBody = z.infer<typeof FunnelCreateBody>;

export const PainPointUpdateBody = z.object({
  status: z.enum(["acknowledged", "tracking", "resolved", "dismissed"]),
});
export type PainPointUpdateBody = z.infer<typeof PainPointUpdateBody>;

export const SignalDefinitionQuery = z.object({
  status: z.enum(["proposed", "backtesting", "shadow", "active", "paused", "retired", "dismissed"]).optional(),
  origin: z.enum(["catalog", "pack", "discovered", "custom"]).optional(),
  tierClass: z.enum(["analytics", "risk"]).optional(),
});
export type SignalDefinitionQuery = z.infer<typeof SignalDefinitionQuery>;

/** A signal written in plain language (or created from a pain point / proposal). */
export const SignalDraftBody = z.object({
  name: z.string().min(3).max(120),
  description: z.string().min(10).max(4000),
  severity: z.enum(SEVERITIES).default("medium"),
  agentIds: z.array(z.string()).max(50).nullable().default(null),
  examples: z
    .array(z.object({ text: z.string().min(1).max(4000), positive: z.boolean(), sessionId: z.string().nullable().default(null) }))
    .max(50)
    .default([]),
  createsIssues: z.boolean().default(false),
  fromPainPointId: z.string().optional(),
  method: z.enum(["rules", "embedding", "decision", "llm_rubric"]).default("decision"),
  /** Initial threshold; calibrated from review labels later. */
  threshold: z.number().min(0).max(1).optional(),
});
export type SignalDraftBody = z.infer<typeof SignalDraftBody>;

export const SignalActionBody = z.discriminatedUnion("action", [
  z.object({ action: z.literal("accept") }),
  z.object({ action: z.literal("dismiss"), reason: z.string().max(1000).optional() }),
  z.object({ action: z.literal("shadow") }),
  z.object({ action: z.literal("activate") }),
  z.object({ action: z.literal("pause") }),
  z.object({ action: z.literal("retire") }),
  z.object({ action: z.literal("nominate") }),
  z.object({
    action: z.literal("edit"),
    description: z.string().min(10).max(4000).optional(),
    severity: z.enum(SEVERITIES).optional(),
    agentIds: z.array(z.string()).max(50).nullable().optional(),
    createsIssues: z.boolean().optional(),
    method: z.enum(["rules", "embedding", "decision", "llm_rubric"]).optional(),
    threshold: z.number().min(0).max(1).optional(),
  }),
]);
export type SignalActionBody = z.infer<typeof SignalActionBody>;

export const ReviewLabelBody = z.object({ label: z.enum(["correct", "incorrect", "unsure"]) });
export type ReviewLabelBody = z.infer<typeof ReviewLabelBody>;

export const InsightUpdateBody = z.object({ status: z.enum(["read", "dismissed"]) });
export type InsightUpdateBody = z.infer<typeof InsightUpdateBody>;

export const AskBody = z.object({ question: z.string().min(3).max(1000) });
export type AskBody = z.infer<typeof AskBody>;

export const VersionCompareQuery = z.object({ a: z.string().min(1), b: z.string().min(1) });

export const EnrichmentSettingsBody = z.object({
  decisionProvider: z.enum(["workers_ai_clef_flash", "workers_ai_clef", "jev", "openai_decisions"]).optional(),
  sampleRate: z.number().min(0).max(1).optional(),
  monthlyBudgetUsd: z.number().int().min(0).max(1_000_000).optional(),
  idleTimeoutMinutes: z.number().int().min(1).max(1440).optional(),
  contentRetentionDays: z.number().int().min(1).max(730).optional(),
});
export type EnrichmentSettingsBody = z.infer<typeof EnrichmentSettingsBody>;

// ─── Business events (public, sent by the SDK alongside traces) ─────────────

/** `track()` / `outcome()` from the SDK. Joined to sessions by session_id (or end_user_id + time). */
export const TrackEventRequest = z.object({
  events: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        agent_id: z.string().min(1).max(200).optional(),
        session_id: z.string().max(200).optional(),
        end_user_id: z.string().max(200).optional(),
        ts: z.iso.datetime().optional(),
        /** For outcome events: the session outcome this event asserts. */
        outcome: SESSION_OUTCOME.optional(),
        value: z.object({ amount: z.number().finite(), currency: z.string().length(3) }).optional(),
        properties: z.record(z.string(), z.union([z.string().max(1000), z.number(), z.boolean()])).default({}),
      }),
    )
    .min(1)
    .max(1000),
});
export type TrackEventRequest = z.infer<typeof TrackEventRequest>;

/** End-user traits for segmentation (`identify()`). */
export const IdentifyRequest = z.object({
  end_user_id: z.string().min(1).max(200),
  traits: z.record(z.string(), z.union([z.string().max(1000), z.number(), z.boolean()])).default({}),
});
export type IdentifyRequest = z.infer<typeof IdentifyRequest>;

export const PainPointCreateBody = z.object({
  kind: z.enum(["friction", "knowledge_gap", "unmet_demand", "failure_mode"]),
  title: z.string().min(3).max(200),
  description: z.string().max(2000).default(""),
  agentIds: z.array(z.string()).min(1).max(50),
  useCaseIds: z.array(z.string()).max(50).default([]),
  /** Example sessions (e.g. from a conversation-path finding). */
  sessionIds: z.array(z.string()).max(50).default([]),
  suggestion: z.string().max(2000).default(""),
});
export type PainPointCreateBody = z.infer<typeof PainPointCreateBody>;

// ─── Integrations ───────────────────────────────────────────────────────────

export const ConnectorCreateBody = z.object({
  kind: z.enum(["stripe", "zendesk", "generic_webhook"]),
  name: z.string().min(2).max(120),
  /** Optional overrides of the default provider → business-event mappings. */
  mappings: z
    .array(z.object({ providerEvent: z.string().min(1).max(120), eventName: z.string().min(1).max(120).regex(/^[^$]/), outcome: z.enum(["resolved", "converted", "partially_resolved", "escalated", "abandoned", "failed", "unknown"]).optional() }))
    .max(50)
    .optional(),
});
export type ConnectorCreateBody = z.infer<typeof ConnectorCreateBody>;

/**
 * Stripe and Zendesk issue their own signing secret when the endpoint is registered there (Stripe
 * `whsec_…`, Zendesk "Reveal secret"); it replaces the generated one. Write-only, never returned.
 */
export const ConnectorSecretBody = z.object({ signingSecret: z.string().min(16).max(256).regex(/^\S+$/, "must not contain whitespace") });
export type ConnectorSecretBody = z.infer<typeof ConnectorSecretBody>;

export const NotificationChannelCreateBody = z
  .object({
    kind: z.enum(["slack", "email", "webhook"]),
    name: z.string().min(2).max(120),
    target: z.string().min(3).max(2000),
    events: z.array(z.string().min(1).max(60)).min(1).max(40),
  })
  .superRefine((b, ctx) => {
    if (b.kind === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(b.target)) ctx.addIssue({ code: "custom", path: ["target"], message: "must be an email address" });
    if (b.kind !== "email" && !/^https:\/\//.test(b.target)) ctx.addIssue({ code: "custom", path: ["target"], message: "must be an https URL" });
    if (b.kind === "slack" && !/^https:\/\/hooks\.slack\.com\//.test(b.target)) ctx.addIssue({ code: "custom", path: ["target"], message: "must be a Slack incoming-webhook URL" });
  });
export type NotificationChannelCreateBody = z.infer<typeof NotificationChannelCreateBody>;

// ─── Accounts ───────────────────────────────────────────────────────────────

const ROLE = z.enum(["owner", "admin", "engineer", "product", "viewer", "underwriter"]);

export const SignupBody = z.object({
  orgName: z.string().trim().min(2).max(120),
  /** URL slug for public pages; derived from the name when omitted. */
  slug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,48}[a-z0-9])?$/).optional(),
  projectName: z.string().trim().min(2).max(120).default("Production agents"),
  dataRegion: z.enum(["us", "eu"]).default("us"),
});
export type SignupBody = z.infer<typeof SignupBody>;

export const ApiKeyCreateBody = z.object({
  name: z.string().trim().min(2).max(80),
  scopes: z.array(z.enum(["ingest", "read", "admin"])).min(1).max(3),
  projectId: z.string().optional(),
  /** ti_live_ keys by default; ti_test_ keys are for non-production environments. */
  environment: z.enum(["live", "test"]).default("live"),
});
export type ApiKeyCreateBody = z.infer<typeof ApiKeyCreateBody>;

export const InviteBody = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  role: ROLE.exclude(["owner"]),
});
export type InviteBody = z.infer<typeof InviteBody>;

export const MemberRoleBody = z.object({ role: ROLE });
export type MemberRoleBody = z.infer<typeof MemberRoleBody>;

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
/** POST /admin/rebuild (docs/17 §7): one derived layer over at most 90 days. */
export const RebuildBody = z
  .object({ layer: z.enum(["signals", "analytics_daily", "dashboard_rollups"]), from: DAY, to: DAY })
  .refine((b) => b.from <= b.to, { message: "`from` must not be after `to`", path: ["from"] })
  .refine((b) => (Date.parse(b.to) - Date.parse(b.from)) / 86_400_000 < 90, { message: "at most 90 days per rebuild", path: ["to"] });
export type RebuildBody = z.infer<typeof RebuildBody>;
